import React from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Button } from "@/components/common/Button";
import { Spinner } from "@/components/common/Spinner";
import type { DraftProgress } from "@/lib/api/apps/types";
import { countLabel, usdMicros } from "../app-format";
import { clock, useNow } from "./draft-model";

/** A round as it drafts: who drafts which version and for how long, the
 *  tokens and their cost so far, what it has done, the call in flight and
 *  any retry. `progress` is null until the round has written any. */
export function DraftProgressPanel({
  progress,
  model,
  version,
  footer,
  onStop,
  stopping = false,
}: {
  progress: DraftProgress | null | undefined;
  model: string;
  version: number;
  footer?: React.ReactNode;
  /** Offered while the round runs. */
  onStop?: () => void;
  stopping?: boolean;
}) {
  const running = !progress || progress.state === "running";
  const now = useNow(running);
  const started = progress?.started_at_ms ?? now;
  const retry = progress?.retry;
  const waiting = progress?.waiting_since_ms;
  return (
    <section
      aria-live="polite"
      className="flex flex-col rounded-app border border-border bg-bg overflow-hidden"
    >
      <div className="flex flex-wrap items-baseline gap-2 px-3 pt-3">
        <span className="text-fg">
          {progress?.state === "done" ? (
            <span className="font-semibold">v{version} is ready</span>
          ) : progress?.state === "failed" ? (
            <span className="font-semibold">{model} stopped</span>
          ) : (
            <>
              <span className="font-semibold">{model}</span> is drafting v
              {version}
            </>
          )}
        </span>
        <span className="text-mono-small-muted">{clock(now - started)}</span>
      </div>
      <div className="flex flex-wrap gap-4 px-3 pt-1 pb-3 text-mono-small-muted">
        <span>
          <span className="text-fg">
            {countLabel(progress?.tokens_in ?? 0)}
          </span>{" "}
          in
        </span>
        <span>
          <span className="text-fg">
            {countLabel(progress?.tokens_out ?? 0)}
          </span>{" "}
          out
        </span>
        <span className="text-fg">{usdMicros(progress?.micros ?? 0)}</span>
      </div>
      <ul className="flex flex-col border-t border-border px-2 py-1 m-0">
        {(progress?.steps ?? []).map((step, i) => (
          <li
            key={i}
            className="list-none flex items-start gap-2 px-1 py-1 rounded-app"
          >
            {step.tone === "warn" ? (
              <WarningAmberOutlinedIcon
                sx={{ fontSize: 14 }}
                className="text-warning mt-0.5"
              />
            ) : (
              <CheckCircleOutlineIcon
                sx={{ fontSize: 14 }}
                className="text-success mt-0.5"
              />
            )}
            <span className="flex-1 min-w-0 text-fg-regular">
              {step.text}
              {step.detail && (
                <span className="text-mono-small-muted"> · {step.detail}</span>
              )}
            </span>
            <span className="text-mono-small-muted">{clock(step.at_ms)}</span>
          </li>
        ))}
        {running && retry && (
          <li className="list-none flex items-start gap-2 px-1 py-1 rounded-app bg-warning/10">
            <HourglassTopOutlinedIcon
              sx={{ fontSize: 14 }}
              className="text-warning mt-0.5"
            />
            <span className="flex-1 min-w-0 text-fg-regular">
              Waiting for {model}
              <span className="text-mono-small-muted"> · {retry.note}</span>
            </span>
          </li>
        )}
        {running && !retry && (
          <li className="list-none flex items-center gap-2 px-1 py-1">
            <Spinner size="xs" />
            <span className="flex-1 min-w-0 text-fg-muted-regular-small">
              {waiting
                ? `Waiting for ${model}`
                : progress
                  ? "Working"
                  : "Starting"}
            </span>
            {waiting && (
              <span className="text-mono-small-muted">
                {clock(now - waiting)}
              </span>
            )}
          </li>
        )}
      </ul>
      {(footer || (running && onStop)) && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-t border-border bg-bg-subtle">
          <span className="flex-1 min-w-0">{footer}</span>
          {running && onStop && (
            <Button
              size="sm"
              variant="secondary"
              disabled={stopping}
              onClick={onStop}
            >
              {stopping ? "Stopping…" : "Stop"}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
